import {useEffect,useLayoutEffect,useRef,useState,type ReactNode} from 'react';

// Catalogue data already arrives through the shared draw stream. Reveal only
// another batch near the viewport; money updates do not restart the observer.
export function LazyCardGrid<T extends {id:string}>({items,queryKey,batchSize,footerActive=false,renderCard,renderGrid}:{items:T[];queryKey:string;batchSize:number;footerActive?:boolean;renderCard:(item:T)=>ReactNode;renderGrid?:(children:ReactNode,count:number)=>ReactNode}){
 const size=Math.max(1,Math.floor(batchSize)),root=useRef<HTMLDivElement>(null),end=useRef<HTMLDivElement>(null),previous=useRef(queryKey);
 const [batch,setBatch]=useState({key:queryKey,count:size});
 const count=Math.min(items.length,batch.key===queryKey?batch.count:size),more=count<items.length;
 const reveal=()=>setBatch(current=>({key:queryKey,count:Math.min(items.length,(current.key===queryKey?current.count:size)+size)}));
 useLayoutEffect(()=>{
  if(previous.current===queryKey)return;previous.current=queryKey;setBatch({key:queryKey,count:size});
  const element=root.current;if(!element)return;
  const dialog=element.closest<HTMLElement>('[data-dialog-scroll],dialog'),toolbar=element.closest('.room-cards')?.querySelector('.room-purchase-toolbar');
  const heading=element.previousElementSibling;
  const top=(heading?.matches('.card-heading-controls')?heading:element).getBoundingClientRect().top,edge=toolbar?.getBoundingClientRect().bottom??dialog?.getBoundingClientRect().top??0;
  // Keep the list heading visible when a scrolled catalogue changes filters.
  if(top<edge){const delta=top-edge;if(dialog)dialog.scrollTop+=delta;else globalThis.scrollBy(0,delta);}
 },[queryKey,size]);
 // Opening the purchase footer must not cover the card just selected.
 // Scroll only on its first appearance; later stake changes do not move the list.
 useLayoutEffect(()=>{
  if(!footerActive)return;
  const element=root.current,card=element?.querySelector<HTMLElement>('.draft-selected');
  const dialog=element?.closest<HTMLElement>('dialog');
  const footer=(dialog??document).querySelector<HTMLElement>('.selection-tray');
  if(!card||!footer)return;
  const rect=card.getBoundingClientRect(),edge=footer.getBoundingClientRect().top;
  if(rect.top<0||rect.top>innerHeight||rect.bottom<=edge)return;
  const delta=rect.bottom-edge;
  if(dialog)dialog.scrollTop+=delta;else globalThis.scrollBy(0,delta);
 },[footerActive]);
 useEffect(()=>{
  const sentinel=end.current;if(!sentinel||!more)return;
  if(typeof IntersectionObserver==='undefined'){setBatch({key:queryKey,count:items.length});return;}
  const observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting))reveal();},{root:sentinel.closest('[data-dialog-scroll],dialog'),rootMargin:'0px 0px 500px 0px'});
  observer.observe(sentinel);return()=>observer.disconnect();
 },[queryKey,count,more,items.length,size]);
 const children=items.slice(0,count).map(renderCard);
 return <div ref={root} className="lazy-card-list" data-shown={count} data-total={items.length} onFocusCapture={event=>{if(more&&event.target.closest('.bingo-card')===event.currentTarget.querySelector('.card-grid')?.lastElementChild)reveal();}}>
  {renderGrid?renderGrid(children,count):<div className="card-grid">{children}</div>}
  {items.length>0&&<div ref={end} className="card-list-sentinel" aria-hidden="true"/>}
 </div>;
}
